import { redactSensitive, VOICE_CONTINUE_SENTINEL, type ContinuationReason, type SessionEventName, type WidgetBranding } from "@ai-guidance/shared";
import { RuntimeApi, isConfigNotAvailable, type SdkOptions } from "./api";
import { currentPagePath } from "./page";
import {
  loadBuilderSession,
  saveBuilderSession,
  takeBuilderFragmentFromUrl,
  type StoredBuilderSession,
} from "./builderSession";
import { installActionTrail } from "./actionTrail";
import {
  appendChatMessage,
  clearChatHistory,
  exportChatState,
  loadActionTrail,
  loadChatHistory,
  restoreChatState,
  setPendingContinuation,
  takePendingContinuation,
} from "./chatHistory";
import {
  adoptChatSessionId,
  ensureChatSessionId,
  peekChatSessionId,
  resetChatSessionId,
} from "./chatSession";
import type { ChatSurface } from "./chatSurface";
import { OrbChat } from "./orbchat";
import { encodeChatCarry, takeChatCarryFromUrl, withChatCarry } from "./chatHandoff";
import { loadWidgetState, saveWidgetState } from "./widgetState";
import { snapshotChanged } from "./continuation";
import { ActionExecutor } from "./executor";
import { isExcludedPath } from "./exclusion";
import {
  disarmSpotlightActivation,
  dismissSpotlight,
  onSpotlightActivate,
  setChatObstacle,
  setGuidanceOutcomeSink,
} from "./guide";
import { COMMERCE_CSS, CommerceRuntime, commerceText, createCommerceAdapter } from "./commerce";
import {
  NUDGE_CSS,
  NudgeBalloon,
  ProactivityEngine,
  nudgeDismissLabel,
  nudgeLanguage,
  nudgeText,
  type NudgeTrigger,
} from "./proactivity";
import type { CommerceTextWireAction } from "./commerce/wire";
import { capturePageSnapshot, setCommerceSnapshot, setSnapshotExclusions } from "./snapshot";
import { stickyUrlValue } from "./stickyParam";
import { chromeCopy, type ChromeCopy } from "./uiText";
import { Widget } from "./widget";
import { VoiceController, type VoiceControllerOptions } from "./voice/controller";
import { DEFAULT_VOICE_NAME, VOICE_PARAM, isVoiceParam, voiceNameFromParam, voiceNameFromSetting, type VoiceName } from "./voice/voiceName";

/**
 * Stratul de prezență (orb + waveform) e deocamdată opt-in prin URL, nu prin
 * configul appului: e pregătirea modului de voce și se testează pe staging,
 * peste HTTPS. Un query param, nu localStorage - ca să nu rămână aprins pe
 * situl cuiva după o singură vizită, și ca fiecare test să înceapă curat.
 *
 * Când A/B-ul dintre provideri e gata, alegerea se mută în configul per-app
 * (`resolveWidgetPresentation`), iar asta rămâne doar ca override de depanare.
 */
function presenceRequested(): boolean {
  return urlFlag("aig_presence");
}

/**
 * Orb-chatul (`orbchat/`): orbul care se morfează în card, ÎN LOCUL panoului
 * clasic. De la 19 aug 2026 e o SETARE PER-APP (`branding.orbChat`, bifată în
 * dashboard la Widget branding) - implicit stinsă, deci niciun sit existent nu
 * se schimbă fără decizia proprietarului. `?aig_orb_chat=1` rămâne override de
 * depanare, lipicios pe durata tabului: aprinde orbul pe un app care nu l-a
 * bifat, ca să-l poți arăta cuiva fără să-i atingi configul.
 *
 * Aprins, montează `OrbChat`; stins, absolut nimic nu se schimbă - `Widget`
 * rămâne exact ce era, până la ultima clasă CSS.
 */
function orbChatRequested(): boolean {
  return urlFlag("aig_orb_chat");
}

/**
 * Vocea agentului, în ordinea priorității: `?voice=1|2|3` din URL (override de
 * test, era `?aig_voice=` până pe 19 aug 2026), apoi setarea per-app din
 * dashboard (`branding.voice`), apoi vocea agentului. Vezi `voice/voiceName.ts`
 * pentru ambele mapări.
 *
 * Parametrul e LIPICIOS pe durata tabului. Motivul e concret, nu comoditate: o
 * conversație vocală navighează prin magazin (`open_search`, `navigate`),
 * fiecare navigare descarcă documentul și pierde query-ul - deci vocea s-ar
 * schimba în mijlocul propoziției. `sessionStorage`, ca la `urlFlag`: moare
 * odată cu tabul, deci nu rămâne agățat de situl clientului. URL-ul, când e
 * prezent, bate întotdeauna ce e în storage.
 */
function voiceNameRequested(branding?: WidgetBranding): VoiceName {
  const fromUrl = stickyUrlValue(VOICE_PARAM, isVoiceParam);
  if (fromUrl !== null) return voiceNameFromParam(fromUrl);
  return voiceNameFromSetting(branding?.voice) ?? DEFAULT_VOICE_NAME;
}

/**
 * Semnele unui refuz de microfon rămase în TEXTUL erorii, când `name` s-a
 * pierdut la împachetare. Deliberat scurte și case-insensitive: formulările
 * diferă de la browser la browser și de la o versiune de SDK la alta.
 */
const MIC_DENIED_HINTS = /permission denied|notallowed|not allowed|denied by user/i;

/**
 * Ce citește vizitatorul când vocea nu pornește.
 *
 * Distincția care contează e microfonul blocat: e singurul eșec pe care îl poate
 * repara EL, și singurul complet mut - odată refuzată permisiunea pentru un site,
 * browserul nu mai afișează nicio cerere, deci butonul pare pur și simplu stricat.
 * Un „something went wrong" acolo îl trimite să caute în locul greșit.
 *
 * Exportată doar ca să fie testabilă direct (`voiceErrorMessage.test.ts`) -
 * apelantul real e tot aici, în `sdk.ts`.
 */
/**
 * Fereastra din interiorul unei aplicații de mesagerie/social (WhatsApp pe
 * Android, Instagram, Facebook, Messenger): un webview în care sistemul NU dă
 * acces la microfon, indiferent câte „Allow" apasă omul și oricâte refresh-uri
 * face - exact simptomul raportat de testeri pe 11 aug 2026. Detecția e pe
 * user-agent, deci acoperă marcajele cunoscute, nu orice webview posibil
 * (WhatsApp pe iOS deschide un Safari-view fără niciun marcaj); pentru restul
 * rămâne mesajul generic de microfon blocat, care e și el adevărat.
 */
function inAppBrowser(): boolean {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|FB_IAB|Instagram|Messenger|Line\/|MicroMessenger|Snapchat|TikTok|; wv\)/i.test(ua);
}

export function voiceErrorMessage(err: Error, copy: ChromeCopy): string {
  if (err.message === "voice_not_configured") return copy.voiceNotConfigured;
  // Refuzul de microfon ÎNTR-UN webview de aplicație nu e o permisiune de
  // reparat, e o fereastră de părăsit - instrucțiunea corectă e „deschide în
  // browser", nu „permite și reîncarcă".
  if (
    (err.message === "mic_denied" || err.name === "NotAllowedError" || MIC_DENIED_HINTS.test(err.message)) &&
    inAppBrowser()
  ) {
    return copy.voiceInAppBrowser;
  }
  // Plafonul de conversații SIMULTANE ale contului, nu o cheie lipsă - vezi
  // `voice/controller.ts` → `start()`. Butonul rămâne funcțional: mesajul
  // trimite spre chatul scris, nu spre „încearcă din nou peste puțin".
  if (err.message === "voice_busy") return copy.voiceBusy;
  // Minutele de voce ale vizitatorului s-au terminat (F-06): nu o defecțiune -
  // conversația continuă în scris, cu transcriptul deja în fir.
  if (err.message === "voice_quota_exhausted") return copy.voiceMinutesOver;
  // `NotAllowedError` = refuz venit direct din getUserMedia, pe browserele fără
  // Permissions API pentru microfon (sau când refuzul tocmai s-a întâmplat).
  // Reîncărcarea e parte din instrucțiune, nu un detaliu: o permisiune schimbată
  // din bara de adrese NU se aplică paginii deja deschise. Fără propoziția asta,
  // omul deblochează, apasă din nou, primește același refuz și crede că a stricat
  // ceva - exact drumul pe care l-am parcurs pe 8 aug 2026 ca să găsim cauza.
  if (err.message === "mic_denied" || err.name === "NotAllowedError") {
    return copy.voiceMicBlocked;
  }
  if (err.name === "NotFoundError") return copy.voiceNoMic;
  // Plasa de siguranță pentru erorile ÎMPACHETATE: SDK-ul providerului prinde
  // refuzul din `getUserMedia` și îl re-aruncă drept eroare proprie, deci `name`
  // nu mai spune nimic, iar vizitatorul primea „a picat ceva" exact în singurul
  // caz pe care îl putea repara singur (staging, 10 aug 2026). Cerem microfonul
  // noi, înainte (vezi `voice/controller.ts` → `start`), deci pe calea normală
  // nu se mai ajunge aici - dar orice alt drum spre provider merită același
  // mesaj corect.
  if (MIC_DENIED_HINTS.test(err.message)) return copy.voiceMicBlocked;
  return copy.voiceFailed;
}

/**
 * Flagurile de test se aprind din URL și rămân aprinse pe durata TABULUI.
 *
 * Lipiciozitatea nu e comoditate: demo-urile pe care testăm (Orbit CRM) sunt
 * SPA-uri care își rescriu URL-ul la fiecare navigare, deci parametrul ar
 * dispărea după primul click și vocea s-ar stinge în mijlocul testului.
 *
 * `sessionStorage`, nu `localStorage`: moare odată cu tabul. Un flag care
 * supraviețuiește închiderii browserului ar rămâne aprins pe situl cuiva.
 */
function urlFlag(name: string): boolean {
  try {
    if (new URLSearchParams(location.search).get(name) === "1") {
      sessionStorage.setItem(name, "1");
      return true;
    }
    return sessionStorage.getItem(name) === "1";
  } catch {
    // `location.search` sau `sessionStorage` inaccesibile (sandbox exotic, cookies
    // blocate) nu au voie să oprească widgetul: fără flag, dar cu chat.
    return false;
  }
}

/**
 * Câte continuări automate poate declanșa o întrebare. Aceeași cifră ca
 * MAX_CONTINUATIONS din backend (services/agent.ts) - asta oprește drumul
 * degeaba, ăla e plafonul real (SDK-ul e cod în browserul altcuiva).
 */
const MAX_CONTINUATION_HOPS = 2;

/**
 * Va descărca acest link documentul curent, ducând vizitatorul pe o pagină a
 * NOASTRĂ? Doar atunci merită persistată intenția de continuare - la reload o
 * reluăm. Excludem, în ordine:
 *  - linkurile externe (alt origin): vizita se termină acolo, n-avem ce continua;
 *  - `download`: descarcă un fișier, nu navighează;
 *  - `target="_blank"`: deschide alt tab, pagina curentă rămâne pe loc;
 *  - ancora pură pe aceeași pagină (`#section`): sare în pagină, nu o descarcă.
 * Un href nevalid (javascript:, malformat) cade tot pe `false`.
 */
function willUnloadToSamePage(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute("href");
  if (!href) return false;
  let url: URL;
  try {
    url = new URL(href, location.href);
  } catch {
    return false;
  }
  if (url.origin !== location.origin) return false;
  if (anchor.hasAttribute("download")) return false;
  if (anchor.target === "_blank") return false;
  if (url.pathname === location.pathname && url.hash !== "") return false;
  return true;
}

/** Câte caractere păstrăm dintr-o etichetă de telemetrie (`GuidanceEvent.targetLabel`). */
const TARGET_LABEL_MAX_CHARS = 80;

/**
 * Eticheta ultimei ținte evidențiate dintr-un răspuns - folosită DOAR pentru
 * telemetria de „reached" de pe calea in_page_reveal, unde știm exact ce
 * acțiune a produs highlight-ul curent. Text scris de agent/config (title,
 * text), niciodată din DOM-ul vizitatorului - nu e nevoie de `labelFor`.
 */
function lastHighlightLabel(actions: CommerceTextWireAction[]): string | undefined {
  for (let i = actions.length - 1; i >= 0; i--) {
    const action = actions[i];
    if (action.type === "highlight" && action.title) return action.title.slice(0, TARGET_LABEL_MAX_CHARS);
    if (action.type === "tooltip") return action.text.slice(0, TARGET_LABEL_MAX_CHARS);
  }
  return undefined;
}

/** Badge discret care confirmă adminului că e în modul de previzualizare. */
function showAdminBadge(): void {
  const badge = document.createElement("div");
  badge.setAttribute("data-ai-guidance", "admin-badge");
  badge.textContent = "Agent · admin mode";
  badge.style.cssText =
    "position:fixed;left:20px;bottom:20px;z-index:2147483000;background:#0f172a;color:#a5b4fc;" +
    "font:600 11.5px system-ui,sans-serif;padding:6px 12px;border-radius:99px;opacity:.92;pointer-events:none;";
  document.body.appendChild(badge);
}

/**
 * Configuratorul (panoul lateral) se încarcă lazy, doar pentru admin și doar
 * pe desktop - vizitatorii obișnuiți nu descarcă niciodată acest cod.
 */
function loadBuilderModule(opts: SdkOptions, sessionToken: string, scanCarry?: string): void {
  (window as unknown as Record<string, unknown>).__AIG_BUILDER_BOOT__ = {
    appId: opts.appId,
    publicKey: opts.publicKey,
    apiBase: opts.apiBase,
    sessionToken,
    ...(scanCarry ? { scanCarry } : {}),
  };
  const script = document.createElement("script");
  script.src = `${opts.apiBase}/builder.js`;
  document.head.appendChild(script);
}

export async function init(opts: SdkOptions): Promise<void> {
  const api = new RuntimeApi(opts);

  // Handoff de sesiune de chat dintr-un salt cross-subdomeniu (chatHandoff.ts):
  // adoptăm session id-ul + starea conversației ÎNAINTE de orice le citește
  // (fetchConfig trimite session id-ul, widgetul afișează istoricul, iar
  // takePendingContinuation reia ghidarea). Fragmentul se șterge la citire.
  const carried = takeChatCarryFromUrl();
  if (carried) {
    adoptChatSessionId(opts.appId, carried.sid);
    restoreChatState(opts.appId, carried.chat);
    // starea UI (deschis/poziție) trece și ea, ca panoul să rămână exact cum era
    if (carried.ui) saveWidgetState(opts.appId, carried.ui);
  }

  // Singurul loc care leagă abandonurile raportate din guide.ts (armedActivation
  // dezarmată fără reach) de rețea - fire-and-forget, telemetrie de produs, nu
  // trebuie să blocheze sau să întrerupă nimic din fluxul vizitatorului.
  setGuidanceOutcomeSink((outcome, targetLabel) => {
    void api
      .event({
        outcome,
        path: currentPagePath(),
        ...(targetLabel ? { targetLabel } : {}),
        sessionId: ensureChatSessionId(opts.appId),
        t: Date.now(),
      })
      .catch(() => {});
  });

  // handoff-ul din dashboard (#aig=token) devine sesiune de builder locală - la
  // fel, un handoff cărat peste un salt cross-origine al scanului (Scan website)
  // (builder/index.ts → navigateToNext), însoțit de starea scanului (#aigscan=).
  let builderSession: StoredBuilderSession | null = loadBuilderSession(opts.appId);
  const { handoff: handoffToken, scanCarry } = takeBuilderFragmentFromUrl();
  if (handoffToken) {
    try {
      const exchanged = await api.exchangeBuilderToken(handoffToken);
      builderSession = { token: exchanged.sessionToken, expiresAt: exchanged.expiresAt };
      saveBuilderSession(opts.appId, builderSession);
    } catch (err) {
      console.warn("[ai-guidance] builder handoff failed", err);
    }
  }
  if (builderSession) api.setBuilderSession(builderSession.token);

  const isDesktop = window.innerWidth >= 1024;
  if (builderSession && isDesktop) {
    loadBuilderModule(opts, builderSession.token, scanCarry ?? undefined);
  } else if (builderSession) {
    showAdminBadge();
  }

  let config, poweredBy, branding, synced, excludedUrls, excludedSelectors, answersExhausted, allowedOrigins, commerceConfig;
  // Nudge-urile proactive: aprinse doar dacă /config a adus câmpul (backendul
  // îl trimite numai pe appurile care sunt ȘI commerce, ȘI cu proactivitatea
  // pornită de merchant - vezi proactivityConfigFor).
  let proactivityEnabled = false;
  // Vocea: aprinsă doar dacă /config a adus câmpul (flagul per app + cheile
  // providerului pe mediu - vezi runtime.ts). Fals = butonul de microfon nu
  // apare deloc.
  let voiceEnabled = false;
  try {
    // Doar dacă id-ul EXISTĂ deja (tab cu o conversație în curs dintr-o navigare
    // anterioară) - nu-l creăm aici: un vizitator nou, fără nicio interacțiune,
    // nu trebuie să capete un id de sesiune doar pentru că a încărcat pagina.
    const cfg = await api.fetchConfig(peekChatSessionId(opts.appId) ?? undefined);
    // not available = app nepublicat / not-live: stare NORMALĂ, nu o eroare.
    // SDK-ul tace pentru vizitatori (nicio UI); panoul de builder (dacă e
    // încărcat) merge independent. Nu logăm nimic - un asistent în configurare
    // nu trebuie să scrie erori în consola sitului clientului.
    //
    // Excepția: confirmarea de instalare. Consola îi spune clientului că după
    // ce lipește snippetul „badge-ul se face Synced", iar un sit nou e not-live
    // până la „Go live" - fără ack aici, dovada că scriptul rulează n-ar veni
    // niciodată înainte de publicare (regresia 30 oct, C9). Doar la
    // `synced: false`, o singură dată, best-effort, fără să montăm nimic.
    if (isConfigNotAvailable(cfg)) {
      if (!cfg.synced) void api.ackSync().catch(() => {});
      return;
    }
    ({ config, poweredBy, branding, synced, excludedUrls, excludedSelectors, answersExhausted, allowedOrigins } = cfg);
    // Doar appurile de magazin primesc câmpul (docs/plan-commerce-actions.md §5).
    // Absent = tot ce urmează în privința coșului nu se întâmplă niciodată.
    commerceConfig = cfg.commerce;
    if (commerceConfig) api.setCommerce(true);
    proactivityEnabled = cfg.proactivity === true;
    voiceEnabled = cfg.voiceEnabled === true;
  } catch (err) {
    // Eroare REALĂ (5xx, rețea, config invalid): aici merită un log.
    console.error("[ai-guidance] failed to load config, widget disabled", err);
    return;
  }

  // Cota lunară de răspunsuri e epuizată ȘI acest tab n-are o conversație în
  // curs: un vizitator nou n-are ce continua, deci nu montăm nimic - fără DOM,
  // fără bulină, deci fără niciun /ask care oricum ar fi degradat de backend
  // (services/answerGrace.ts). Un tab care AVEA deja o conversație trece mai
  // jos: alocația de grație de acolo îl lasă s-o termine.
  if (answersExhausted && loadChatHistory(opts.appId).length === 0) return;

  // Confirmă o singură dată că scriptul chiar rulează pe situl clientului -
  // niciun apel suplimentar odată ce backend-ul a înregistrat unul (synced
  // nu se mai resetează la false). Best-effort: un eșec de rețea aici nu
  // trebuie să oprească widgetul pentru vizitator.
  if (!synced) void api.ackSync().catch(() => {});

  // Suprafața de conversație: panoul clasic sau orb-chatul, sub flag. Tot ce
  // urmează vorbește prin `ChatSurface`, deci nimic din plumbing nu se dublează.
  // Orb-chatul își aduce prezența singur (orbul E interfața lui), deci nu mai
  // trece prin `aig_presence`.
  // Limba în care îi vorbim vizitatorului - o singură dată, pentru toată
  // suprafața: chrome, note de eroare, salutul implicit. Vezi `uiText.ts`.
  const copy = chromeCopy(config.business?.language);

  // Setarea appului SAU flagul de tab (override de depanare) - vezi
  // orbChatRequested. Orb-chatul are o singură înfățișare, HUD (decizie de
  // proprietar, 19 aug 2026 - fostul `?aig_skin=hud` a devenit singura piele).
  const orbChatOn = orbChatRequested() || branding.orbChat === true;
  const widget: ChatSurface = orbChatOn
    ? new OrbChat(opts.appId, poweredBy, branding, config.business?.language, opts.bottomOffset)
    : new Widget(opts.appId, poweredBy, branding, presenceRequested(), config.business?.language);
  const executor = new ActionExecutor(config, widget, () => {
    // Ținta lipsă e un abandon de facto al pasului de ghidare - același drum
    // fire-and-forget ca abandonurile din guide.ts, fără etichetă (nu există
    // element de etichetat).
    void api
      .event({
        outcome: "target_missing",
        path: currentPagePath(),
        sessionId: ensureChatSessionId(opts.appId),
        t: Date.now(),
      })
      .catch(() => {});
  });

  // Un link duce spre alt subdomeniu DECLARAT al aceluiași bot? (allowedOrigins,
  // din /config). Same-origin nu intră aici (sessionStorage îl duce oricum);
  // extern nici - nu cărăm sesiunea în afara siturilor tale.
  const sameBotOrigin = (u: URL): boolean =>
    u.origin !== location.origin && (allowedOrigins ?? []).includes(u.origin);

  // Handoff de sesiune de chat la un salt cross-subdomeniu: sessionStorage e
  // per-origine, deci fără asta conversația ar reporni pe alt subdomeniu al tău.
  // Interceptăm DOAR left-click-ul simplu pe un link same-bot (fără modificatori,
  // fără tab nou) și cărăm session id-ul + starea conversației prin fragmentul
  // URL-ului țintă. Restul (modificatori, tab nou, extern) navighează normal.
  // Bubble-phase, deci rulăm DUPĂ handlerul de ghidare de pe element (care poate
  // arma „pending"-ul) - starea cărată îl include. Vezi chatHandoff.ts.
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const anchor = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;
    let url: URL;
    try {
      url = new URL(anchor.getAttribute("href") ?? "", location.href);
    } catch {
      return;
    }
    if (!sameBotOrigin(url)) return;
    // Doar dacă tab-ul are DEJA o conversație de cărat (id scris = a interacționat).
    const sid = peekChatSessionId(opts.appId);
    if (!sid) return;
    e.preventDefault();
    const carry = encodeChatCarry({ sid, chat: exportChatState(opts.appId), ui: loadWidgetState(opts.appId) });
    location.href = withChatCarry(url, carry);
  });

  // Pagină exclusă de proprietarul sitului (settings → Excluded URLs): widgetul
  // e invizibil și - garda din runAsk - nu se capturează niciun snapshot.
  const onExcludedPage = (): boolean => isExcludedPath(currentPagePath(), excludedUrls);

  // Ținut pe toată durata tab-ului, nu doar la /config: `/config` se cere o
  // singură dată per tab, deci un tab deschis ÎNAINTE ca luna să se epuizeze
  // n-ar mai afla niciodată altfel - ✕-ul lui n-ar declanșa dispariția.
  // Reîmprospătat mai jos din câmpul `answersExhausted` al fiecărui răspuns /ask.
  let tabExhausted = answersExhausted;
  // Devine `true` la închiderea explicită (✕) cât timp tab-ul e epuizat - de
  // atunci widgetul rămâne ascuns definitiv în acest tab (istoricul e golit
  // odată cu asta, deci un reload cade pe ramura de „nu montăm").
  let hiddenForExhaustion = false;

  // Un singur punct care decide vizibilitatea, ca excluderea pe URL și
  // epuizarea contului să nu se calce pe picioare apelând `setHidden` separat
  // cu valori contradictorii - a doua condiție ar rescrie starea primeia.
  const applyHiddenState = (): void => {
    const hidden = onExcludedPage() || hiddenForExhaustion;
    widget.setHidden(hidden);
    if (hidden) {
      // fără resturi de ghidare peste o pagină pe care nu existăm (sau un
      // widget pe care vizitatorul tocmai l-a închis definitiv) - o activare
      // armată nu mai are la ce se aștepta, deci e un abandon.
      disarmSpotlightActivation("abandoned_dismissed");
      dismissSpotlight();
    } else {
      // Navigare SPA de pe o pagină exclusă pe una permisă: ABIA ACUM widgetul
      // se arată vizitatorului, deci abia acum e o „afișare".
      reportWidgetShown();
    }
  };

  /**
   * Pâlnia de sesiune (vizite → deschideri): două semnale fire-and-forget, o
   * singură dată per ÎNCĂRCARE DE PAGINĂ. Flagurile stau în memorie, în
   * închiderea lui `init` - NIMIC nu se scrie în browserul vizitatorului pentru
   * măsurare (nici o cheie nouă, nici un câmp nou în `aig_widget_ui_`): un
   * marcaj de telemetrie în storage n-ar servi serviciul cerut de vizitator și
   * ar slăbi „strictly necessary" din cookies §1. Deduplicarea reală („o vizită
   * o singură dată") se face SERVER-side pe (appId, sessionId, zi).
   *
   * Închidere, nu variabilă de modul: `init` rulează o singură dată per
   * încărcare de pagină (`__AIG_BOOTED__` din index.ts), deci e același lucru -
   * dar fără stare care supraviețuiește între rulări (teste, re-boot manual).
   *
   * Fără `path`, deliberat - vezi sessionEventSchema în shared.
   *
   * `sessionId` e opțional aici, deliberat: `widget_shown` NU are voie să
   * creeze un id - vezi chatSession.ts - deci poartă id-ul doar dacă exista
   * deja (tab cu o conversație începută anterior). `widget_opened` vine
   * întotdeauna DUPĂ o interacțiune (click pe bulă, sau redeschiderea unei
   * conversații existente la montare), deci acolo id-ul se poate crea.
   */
  let shownReported = false;
  let openedReported = false;
  const reportSessionEvent = (
    event: SessionEventName,
    sessionId: string | undefined,
    trigger?: NudgeTrigger,
  ): void => {
    void api
      .event({ event, ...(sessionId ? { sessionId } : {}), ...(trigger ? { trigger } : {}), t: Date.now() })
      .catch(() => {});
  };
  /**
   * `/config` se cere ȘI pe paginile excluse (excluderea e client-side, după
   * sosirea configului), deci „widgetul s-a afișat" NU se poate deduce din boot:
   * se raportează doar când widgetul chiar e vizibil pe o pagină neexclusă.
   */
  function reportWidgetShown(): void {
    if (shownReported || onExcludedPage() || hiddenForExhaustion) return;
    shownReported = true;
    reportSessionEvent("widget_shown", peekChatSessionId(opts.appId) ?? undefined);
  }
  function reportWidgetOpened(): void {
    if (openedReported) return;
    openedReported = true;
    reportSessionEvent("widget_opened", ensureChatSessionId(opts.appId));
  }

  /**
   * Salutul afișat pe un tab proaspăt (mai jos, lângă `loadChatHistory`) NU se
   * scrie în sessionStorage la montare - asta ar fi exact stocarea pe care o
   * evităm pentru vizitatorul care încă n-a interacționat. Rămâne „în așteptare"
   * (doar în DOM, via `widget.addMessage`) până la prima interacție reală
   * (deschiderea panoului sau prima întrebare) - abia atunci se persistă, ca
   * reload-ul din acel moment înainte să găsească aceeași conversație.
   * `interactionStarted` acoperă și cazul rar în care restaurarea stării
   * salvate deschide panoul chiar la montare (widget.ts:382), ÎNAINTE ca acest
   * bloc să apuce să calculeze salutul: atunci persistăm imediat ce salutul e
   * cunoscut, mai jos.
   */
  let interactionStarted = false;
  let pendingGreeting: string | null = null;
  function persistPendingGreeting(): void {
    if (pendingGreeting === null) return;
    appendChatMessage(opts.appId, { role: "agent", text: pendingGreeting });
    pendingGreeting = null;
  }
  /**
   * Punctul unic al primei interacțiuni: persistă salutul în așteptare,
   * scrie în sessionStorage trail-ul de acțiuni acumulat până acum doar în
   * memorie (actionTrail.ts) ȘI migrează contoarele motorului de nudge din
   * memorie în sessionStorage (proactivity/engine.ts → G24) - aceeași ocazie,
   * aceeași justificare legală (ePrivacy art. 5(3)). Apelată din `onAsk`,
   * `onOpen`, din `onEngage`-ul unui nudge (mai jos) și din cazul rar de mai
   * jos (restaurare cu panoul deja deschis la montare).
   */
  function markInteractionStarted(): void {
    interactionStarted = true;
    persistPendingGreeting();
    actionTrail.flush();
    proactivity?.notifyInteractionStarted();
  }

  // Zonele excluse din snapshot (settings → Excluded content) se armează o
  // singură dată, înainte de orice captură - inclusiv cea a unei continuări
  // reluate din sessionStorage mai jos.
  setSnapshotExclusions(excludedSelectors);

  // Trail-ul pornește ACUM, nu la prima întrebare - drumul de dinainte de
  // deschiderea chatului e exact contextul pe care vrem să-l aibă modelul.
  // După setSnapshotExclusions: click-urile din zone excluse nu se rețin.
  // Colectarea e imediată, dar scrierea în sessionStorage NU: `actionTrail`
  // ține totul în memorie până la `flush()`, apelat mai jos în ACELEAȘI
  // puncte ca `persistPendingGreeting` - prima interacțiune explicită.
  const actionTrail = installActionTrail(opts.appId, onExcludedPage);

  /**
   * Un tur de întrebare: capturează pagina vie, întreabă, arată răspunsul,
   * execută acțiunile. Același drum pentru întrebarea pusă de om și pentru
   * continuarea de după o navigare - diferă doar de unde vine întrebarea și
   * dacă se contorizează ca răspuns.
   */
  /**
   * Partea de commerce (adaptor Shopify + carduri + coș) și conversația vocală
   * se nasc amândouă DUPĂ `widget.mount()` - prima are nevoie de shadow root,
   * a doua de stratul de prezență. Declarate aici fiindcă `runAsk` și captura de
   * pagină le citesc, iar ele sunt definite mai sus.
   */
  let commerce: CommerceRuntime | null = null;
  let voice: VoiceController | null = null;
  /** Bagajul de opțiuni al vocii - singurul câmp rescris e `sessionId`, la reset. */
  let voiceSessionRef: { sessionId: string } | null = null;
  /** Motorul de nudge-uri, doar pe magazinele cu proactivitatea pornită (mai jos). */
  let proactivity: ProactivityEngine | null = null;
  /** Balonul lui - ținut aici ca `onOpen` (definit mai sus) să-l poată stinge. */
  let nudgeBalloon: NudgeBalloon | null = null;
  /**
   * Triggerul nudge-ului din care s-a deschis conversația, în AȘTEPTAREA primei
   * întrebări reale. Click-ul pe balon NU trimite nimic la model (zero cereri
   * LLM până nu scrie omul, docs/plan-commerce-proactivity.md): el doar deschide
   * panoul cu propoziția balonului deja în fir. Abia întrebarea vizitatorului
   * pleacă spre /ask, purtând triggerul - o singură dată, apoi se stinge:
   * contextul „ai fost adus aici de un nudge" e adevărat despre PRIMA tură, nu
   * despre toată conversația.
   */
  let pendingNudge: NudgeTrigger | null = null;

  const runAsk = async (
    question: string,
    continuation: { hops: number; reason?: ContinuationReason } | null,
  ): Promise<void> => {
    // Punctul unic prin care pleacă snapshotul paginii: pe o pagină exclusă
    // nu iese NIMIC - nici întrebarea omului (widgetul e oricum ascuns), nici
    // o continuare automată sosită dintr-o navigare de pe o pagină permisă.
    if (onExcludedPage()) return;
    // Fiecare întrebare nouă închide firul precedent: un răspuns care NU
    // evidențiază nimic n-ar apuca să dezarmeze activarea rămasă de la pasul
    // dinainte (doar `highlightElement` o face), iar un click întârziat pe
    // vechea țintă ar continua un fir pe care agentul l-a abandonat deja.
    // O continuare automată (`continuation` nenul - in_page_reveal/navigate)
    // NU e o „întrebare nouă" a vizitatorului: e chiar pasul următor al
    // aceleiași ghidări, deci nu emite abandon aici.
    disarmSpotlightActivation(continuation ? undefined : "abandoned_new_question");

    // Un controller per întrebare: butonul Stop îl anulează, ceea ce închide
    // conexiunea și oprește apelul la model în backend (routes/runtime.ts).
    const controller = new AbortController();
    widget.onStop(() => {
      // Dacă răspunsul a sosit și doar se derulează, Stop încheie derularea;
      // altfel anulează cererea. Un singur buton, două momente diferite.
      widget.finishReveal();
      controller.abort();
    });
    widget.setBusy(true);
    try {
      const captured = capturePageSnapshot();
      // harta trebuie legată de ACEASTĂ întrebare înainte de răspuns: idx-urile
      // din răspuns se referă la snapshotul trimis cu ea
      executor.setSnapshot(captured);
      // Pe un magazin, ecranul nu e complet fără coș: agentul vede ce e ÎN el la
      // fiecare tură („ai deja două") și află aici, o singură dată, dacă ultima
      // acțiune de coș a eșuat. Captura nu se blochează pe rețea - vezi
      // `snapshotExtras`.
      if (commerce) Object.assign(captured.snapshot, await commerce.snapshotExtras());
      // drumul de până acum (actionTrail.ts) - citit la fiecare întrebare,
      // fiindcă între întrebări trail-ul a continuat să crească
      const recentActions = loadActionTrail(opts.appId);
      // Doar pe o întrebare pusă de OM: o continuare automată e pasul următor al
      // aceleiași ture, iar linia de context s-a consumat deja la prima.
      const nudge = continuation ? null : pendingNudge;
      if (!continuation) pendingNudge = null;
      const response = await api.ask(
        question,
        { path: currentPagePath(), title: document.title },
        ensureChatSessionId(opts.appId),
        {
          snapshot: captured.snapshot,
          ...(recentActions.length > 0 ? { recentActions } : {}),
          ...(nudge ? { nudge } : {}),
          signal: controller.signal,
          ...(continuation
            ? {
                continuation: true,
                ...(continuation.reason ? { continuationReason: continuation.reason } : {}),
              }
            : {}),
        },
      );

      // bula de „gândește" dispare înainte ca răspunsul să înceapă să se așeze
      widget.setBusy(false);
      widget.addMessage("agent", response.message, { reveal: true });
      appendChatMessage(opts.appId, { role: "agent", text: response.message });
      // Fiecare răspuns reîmprospătează starea de epuizare a tab-ului - vezi
      // comentariul de la `tabExhausted`: `/config` nu mai vine a doua oară.
      tabExhausted = response.answersExhausted;

      const hops = continuation ? continuation.hops + 1 : 0;
      // O acțiune care duce singură pagina altundeva (`open_search`) descarcă
      // documentul în conversația SCRISĂ: fără nimic armat, omul ar ajunge pe
      // pagina de rezultate cu un panou mut. Refolosim EXACT mecanismul
      // construit pentru linkul evidențiat - aceeași intenție în sessionStorage,
      // același `takePendingContinuation` la `init`, același plafon de hop-uri -
      // deci pe rezultate agentul reia aceeași întrebare cu snapshotul nou și
      // comentează ce se vede. Se armează DOAR dacă saltul chiar are loc
      // (executorul cheamă callbackul abia atunci) și doar pe text: vocea are
      // protocolul ei în doi timpi, iar un pending orfan i-ar deschide panoul.
      executor.armPageChange(
        !voice?.active && hops < MAX_CONTINUATION_HOPS
          ? () => setPendingContinuation(opts.appId, { question, hops, at: Date.now() })
          : null,
      );
      await executor.run(response.actions);
      executor.armPageChange(null);

      // Un singur fir de continuare, pentru ambele feluri de „pasul următor":
      // vizitatorul apasă exact ce i-am arătat, iar noi reobservăm. Diferă doar
      // dacă acel click descarcă pagina (un link) sau nu (SPA / dezvăluire
      // in-page). Ne abonăm ori de câte ori mai avem hop-uri - plafonul e comun,
      // indiferent dacă pasul traversează pagini sau nu. `onSpotlightActivate`
      // întoarce `false` fără spotlight pe ecran, deci abonamentul se leagă doar
      // dacă răspunsul chiar a evidențiat ceva.
      if (hops < MAX_CONTINUATION_HOPS) {
        const hrefAtAnswer = location.href;
        onSpotlightActivate(
          () => {
            // Pagina a supraviețuit clickului - dar poate doar DEOCAMDATĂ: pe o
            // navigare MPA lentă (server >2s), plafonul dur al ferestrei de
            // liniște trage cât documentul e încă viu, cu DOM neatins. De-aia
            // intenția scrisă la click de `onClickThrough` se consumă DOAR cu
            // dovada că linkul n-a descărcat pagina: ruta s-a schimbat pe loc
            // (router SPA cu pushState) sau DOM-ul s-a mișcat (dezvăluire).
            // Pe drumul tăcut o lăsăm în storage - dacă navigarea e în zbor,
            // sosirea o reia; dacă nu era nicio navigare, TTL-ul o expiră
            // (chatHistory.ts). Fără condiția asta am pierde exact hop-ul
            // ghidat pe conexiunile lente.
            const routedInPlace = location.href !== hrefAtAnswer;
            // `guide.ts` a așteptat deja liniștea DOM-ului (reveal animat/lazy)
            // înainte să cheme asta - recaptura de aici vede pagina stabilizată.
            const fresh = capturePageSnapshot();
            // Click-ul n-a schimbat nimic vizibil (elementul evidențiat nu avea
            // de dezvăluit nimic, sau vizitatorul văzuse deja reveal-ul): nu
            // ardem un apel LLM pentru un răspuns identic cu cel deja arătat.
            const changed = snapshotChanged(captured.snapshot, fresh.snapshot);
            // "reached": vizitatorul chiar a atins ținta indicată - dovedit de
            // aceeași evidență care decide dacă intenția de continuare se
            // consumă acum (rută schimbată sau DOM mișcat), NU la fiecare
            // apel al acestui callback: pe o navigare MPA lentă, plafonul dur
            // poate trage cu documentul încă viu, fără nicio dovadă - acolo
            // intenția rămâne în storage și "reached" se raportează abia la
            // sosirea pe pagina nouă (mai jos, în `init`), o singură dată.
            if (routedInPlace || changed) {
              takePendingContinuation(opts.appId);
              const targetLabel = lastHighlightLabel(response.actions);
              void api
                .event({
                  outcome: "reached",
                  path: currentPagePath(),
                  ...(targetLabel ? { targetLabel } : {}),
                  sessionId: ensureChatSessionId(opts.appId),
                  t: Date.now(),
                })
                .catch(() => {});
            }
            if (!changed) return;
            void runAsk(question, { hops, reason: "in_page_reveal" });
          },
          (el) => {
            // Sincron, la click, înainte de un eventual unload: dacă vizitatorul
            // a apăsat un link care va descărca pagina, întrebarea trebuie să fie
            // deja în sessionStorage când pleacă documentul - la init o reluăm.
            // Same-page (sessionStorage duce „pending"-ul) SAU alt subdomeniu
            // declarat (îl duce handoff-ul de mai jos, care citește starea DUPĂ
            // acest handler, bubble-phase) - în ambele cazuri ghidarea continuă.
            const anchor = el.closest("a[href]") as HTMLAnchorElement | null;
            let dest: URL | null = null;
            if (anchor) {
              try {
                dest = new URL(anchor.getAttribute("href") ?? "", location.href);
              } catch {
                dest = null;
              }
            }
            if (anchor && (willUnloadToSamePage(anchor) || (dest && sameBotOrigin(dest)))) {
              setPendingContinuation(opts.appId, { question, hops, at: Date.now() });
            }
          },
        );
      }
    } catch (err) {
      // Vizitatorul a apăsat Stop: e o alegere a lui, nu o defecțiune. Fără
      // mesaj de eroare, fără log de eroare - doar ne oprim.
      if (controller.signal.aborted) return;
      console.error("[ai-guidance] ask failed", err);
      // O continuare eșuată nu primește mesaj de eroare: omul n-a cerut-o, deci
      // n-ar înțelege ce a eșuat - a primit deja un răspuns la întrebarea lui.
      if (continuation) return;
      const errorText = copy.askFailed;
      widget.addMessage("agent", errorText);
      appendChatMessage(opts.appId, { role: "agent", text: errorText });
    } finally {
      widget.setBusy(false);
    }
  };

  // Închiderea panoului încheie și ghidarea de pe pagină: fără asta rămâne un
  // inel orfan peste situl clientului, iar un click pe el ar redeschide panoul
  // cu pasul următor al unei conversații pe care vizitatorul tocmai a închis-o.
  widget.onClose(() => {
    // ✕ explicit: o activare armată nu va mai fi atinsă din acest fir de chat.
    disarmSpotlightActivation("abandoned_dismissed");
    dismissSpotlight();
    // Închiderea explicită, cât timp tab-ul e epuizat, e definitivă: golim
    // istoricul (viitoarea încărcare din acest tab cade pe ramura de „nu
    // montăm" de la începutul lui `init`) și ascundem widgetul acum, fără să
    // așteptăm reload-ul. NU la restaurarea stării de la montare (mai jos) -
    // acolo n-a închis nimeni nimic, vezi comentariul lui `Widget.onClose`.
    if (tabExhausted) {
      clearChatHistory(opts.appId);
      hiddenForExhaustion = true;
      applyHiddenState();
    }
  });

  widget.onAsk(async (raw) => {
    markInteractionStarted();
    // Datele sensibile (card, IBAN, CNP, email, parole dictate) se maschează
    // AICI, înainte să părăsească browserul - nu ajung nici pe serverul
    // nostru, nici la model. Bula vizitatorului arată versiunea mascată,
    // deliberat: e feedbackul că datele n-au plecat. Serverul redactează și
    // el, cu același cod - stratul lui prinde SDK-uri vechi și apeluri
    // directe (31 aug 2026).
    const { text: question, kinds } = redactSensitive(raw);
    widget.addMessage("user", question);
    // Garanția 100% (cerere Radu): la orice mascare, avertismentul apare
    // DETERMINIST, ca notiță de widget - nu depinde de ce alege modelul să spună.
    if (kinds.length > 0) widget.noticeSensitiveRedacted();
    appendChatMessage(opts.appId, { role: "user", text: question });
    await runAsk(question, null);
  });

  widget.onOpen(() => {
    markInteractionStarted();
    reportWidgetOpened();
    // Panoul deschis = conversația e pe ecran; un balon rămas lângă bulină ar
    // repeta în afara panoului ceva ce e deja în fir.
    nudgeBalloon?.hide();
  });

  widget.mount();
  // Montarea e momentul în care widgetul apare pentru vizitator. Garda de
  // pagină exclusă e ÎN `reportWidgetShown` (nu aici): `applyHiddenState` de
  // mai jos rulează doar când app-ul are excluderi configurate.
  reportWidgetShown();

  // Magazin: adaptorul platformei + stratul de carduri/toast, în shadow root-ul
  // deja montat. Pe orice alt sit blocul ăsta nu rulează, deci nici CSS-ul, nici
  // vreo cerere spre /cart.js nu există.
  if (commerceConfig) {
    widget.addStyle(COMMERCE_CSS);
    // Cardurile de produs din pagină se comprimă la o linie fiecare: catalogul
    // duce oricum titlurile și prețurile la model, captura mai are de spus doar
    // CARE produse sunt pe ecran și în ce ordine (`commerce/snapshotProducts.ts`).
    setCommerceSnapshot(true);
    const shopCopy = commerceText(config.business?.language);
    commerce = new CommerceRuntime(
      createCommerceAdapter(commerceConfig, shopCopy),
      {
        addCardsToThread: (node) => widget.addCardsToThread(node),
        mountOverlay: (node) => widget.mountOverlay(node),
        openChat: () => widget.open(),
        // Citit la fiecare `show_products`, nu o singură dată: vizitatorul poate
        // porni vocea la mijlocul conversației scrise.
        isVoiceActive: () => voice?.active ?? false,
        // Un coș tocmai atins nu e un coș uitat - vezi `stale_cart`. Nul pe
        // magazinele fără proactivitate. Iar VOCEA își împrospătează ecranul de
        // pe sesiune: o mutație de coș nu schimbă calea, deci poll-ul n-ar urca
        // niciodată coșul cel nou, iar tura următoare ar „verifica" pe date
        // stătute (12 aug 2026, staging).
        onCartChanged: () => {
          proactivity?.noteCartActivity();
          voice?.refreshSessionPage();
        },
        // Simetricul pe EȘEC: vizitatorul a văzut toastul de eroare, deci și
        // agentul trebuie să vadă același ecran acum (`lastActionError` din
        // captura proaspătă), nu la următoarea navigare. Fără
        // `noteCartActivity`: coșul chiar nu s-a schimbat.
        onCartActionFailed: () => {
          voice?.refreshSessionPage();
        },
        // Modalul de comparație se deschide peste pagină, deci trebuie să arate
        // ca suprafața din care a plecat - vezi `commerce/compare.ts`.
        hudSkin: orbChatOn,
      },
      shopCopy,
    );
    executor.setCommerce(commerce);
  }

  /**
   * Proactivitatea (docs/plan-commerce-proactivity.md): motorul de triggere +
   * balonul de lângă bulină. Ambele condiții sunt obligatorii - flagul din
   * /config ȘI un runtime de commerce viu: fără el n-ar exista coș din care să
   * se compună textele, iar `stale_cart`/`exit_intent` ar fi mute oricum.
   *
   * Pe orice alt sit blocul nu rulează: niciun CSS injectat, niciun listener,
   * niciun `setInterval`, nicio cerere spre /cart.js.
   */
  if (proactivityEnabled && commerce) {
    const shop = commerce;
    widget.addStyle(NUDGE_CSS);
    const lang = nudgeLanguage(config.business?.language);
    const balloon = new NudgeBalloon((node) => widget.mountOverlay(node));
    nudgeBalloon = balloon;
    proactivity = new ProactivityEngine(opts.appId, {
      now: () => Date.now(),
      path: () => currentPagePath(),
      cartPath: shop.cartPath(),
      readCart: () => shop.readCart(),
      isPanelOpen: () => widget.isOpen(),
      isVoiceActive: () => voice?.active ?? false,
      viewportWidth: () => window.innerWidth,
      onNudge: (trigger, cart) => {
        // Pagina exclusă de proprietar: widgetul e ascuns acolo, deci nici
        // balonul lui n-are ce căuta (aceeași gardă ca la snapshot).
        if (onExcludedPage() || hiddenForExhaustion) return;
        const text = nudgeText(trigger, lang, cart ? { itemCount: cart.itemCount, totalPrice: cart.totalPrice } : undefined);
        balloon.show(text, nudgeDismissLabel(lang), {
          onEngage: () => {
            // Firul pornește LOCAL, fără nicio cerere: propoziția pe care omul
            // tocmai a citit-o devine primul mesaj al asistentului, iar cursorul
            // e deja în compozitor (widget.open focusează). Alternativele mint
            // amândouă - o întrebare sintetică pusă „de vizitator" (n-a spus-o)
            // sau un /ask automat (ar arde un răspuns din cota clientului pentru
            // un text pe care îl scriem noi).
            markInteractionStarted();
            widget.open();
            widget.addMessage("agent", text);
            appendChatMessage(opts.appId, { role: "agent", text });
            pendingNudge = trigger;
            reportSessionEvent("nudge_engaged", ensureChatSessionId(opts.appId), trigger);
          },
          onDismiss: () => {
            proactivity?.dismiss();
            reportSessionEvent("nudge_dismissed", peekChatSessionId(opts.appId) ?? undefined, trigger);
          },
        });
        // Ca `widget_shown`: id-ul se poartă doar dacă EXISTĂ deja - un nudge de
        // `hesitation` apare înaintea oricărei interacțiuni, iar SDK-ul n-are
        // voie să creeze un id de sesiune pentru o măsurătoare (ePrivacy art.
        // 5(3), vezi chatSession.ts).
        reportSessionEvent("nudge_shown", peekChatSessionId(opts.appId) ?? undefined, trigger);
      },
    });
    // Cazul rar de la `interactionStarted` (widget.ts:382): panoul s-a deschis
    // singur la restaurare, ÎNAINTE ca acest motor să apuce să existe - fără
    // asta, prima lui interacțiune reală ar rămâne needetectată, fiindcă nimic
    // nu mai cheamă `markInteractionStarted()` a doua oară.
    if (interactionStarted) proactivity.notifyInteractionStarted();
    proactivity.start();
  }

  // Modul de voce, DUPĂ montare: stratul de prezență se naște în `mount()`, iar
  // fără el n-ar avea ce conduce. Nimic din SDK-ul providerului nu se descarcă
  // până la primul click pe microfon - vezi voice/controller.ts.
  // DOAR pe appurile cu vocea aprinsă (`voiceEnabled` din /config, 24 aug 2026):
  // altfel butonul de microfon nu apare deloc - un buton care duce la un mesaj
  // de eroare e mai rău decât lipsa lui, iar comutatorul per app există tocmai
  // ca un mediu cu chei de provider să nu dea voce fiecărui sit de pe el.
  if (widget.presence && voiceEnabled) {
    const presence = widget.presence;
    // Bagajul de opțiuni e ținut aici, nu inline, dintr-un singur motiv:
    // `sessionId` se poate SCHIMBA sub picioarele controllerului, când
    // vizitatorul apasă „o iau de la capăt". Controllerul îl citește la
    // pornirea fiecărei conversații, deci o conversație vocală pornită după
    // reset trebuie să găsească id-ul NOU - altfel modelul ar continua exact
    // firul pe care omul tocmai l-a șters.
    const voiceOptions: VoiceControllerOptions = {
      appId: opts.appId,
      publicKey: opts.publicKey,
      apiBase: opts.apiBase,
      sessionId: ensureChatSessionId(opts.appId),
      presence,
      // ACELAȘI executor ca la chatul scris. Asta e tot rostul integrării:
      // spotlight-ul se aprinde pe pagina reală în timp ce agentul vorbește -
      // „uite unde" chiar arată unde.
      runActions: (actions) => executor.run(actions),
      // ACELAȘI snapshot ca la chatul scris, cu aceeași legare a hărții
      // `idx → nod DOM` de executor - acțiunile turei următoare țintesc exact
      // ce s-a trimis. Pe o pagină exclusă nu pleacă niciun snapshot (aceeași
      // gardă ca în runAsk), dar calea tot se urcă - agentul măcar știe UNDE e.
      capturePage: async () => {
        if (onExcludedPage()) return { path: currentPagePath(), title: document.title };
        const captured = capturePageSnapshot();
        executor.setSnapshot(captured);
        // Aceeași completare ca pe calea text: coșul real + eventualul eșec
        // nelivrat. Pe un sit fără commerce, `await`-ul ăsta nu există.
        if (commerce) Object.assign(captured.snapshot, await commerce.snapshotExtras());
        return { path: currentPagePath(), title: document.title, snapshot: captured.snapshot };
      },
      onCaption: (role, text) => {
        // Sentinelul de reluare e un mesaj tehnic pe care îl trimitem NOI
        // providerului după o navigare - vizitatorul nu l-a rostit, deci nu
        // are ce căuta nici sub orb, nici în istoricul conversației.
        if (text.trim() === VOICE_CONTINUE_SENTINEL) return;
        presence.setCaption(role, text);
        // Transcriptul intră și în panou, ca vizitatorul să-l regăsească după
        // ce închide vocea. Captionul e efemer; conversația, nu.
        widget.addMessage(role === "user" ? "user" : "agent", text);
        appendChatMessage(opts.appId, { role: role === "user" ? "user" : "agent", text });
      },
      onError: (message) => console.warn("[ai-guidance] voice:", message),
      // `?voice=` / `branding.voice` sunt deja traduse și validate aici
      // (`voiceName.ts`), deci spre controller pleacă o cheie din setul
      // ElevenLabs sau „agent" - singura variantă rămasă de la 9 aug 2026
      // (verdictul A/B-ului).
      voiceName: voiceNameRequested(branding),
      onStateChange: (state) => {
        if (state === "idle") widget.setVoiceActive(voice?.active ?? false);
      },
      // Reluarea de după navigare blocată de browser (Safari cere gest pentru
      // microfon): sesiunea trăiește, tap-ul pe microfon o continuă - nota îi
      // spune vizitatorului exact asta, în loc să-l lase cu o conversație
      // „închisă singură" (F-03, feedback 24 aug 2026).
      onResumeBlocked: () => widget.addNotice(copy.voiceResumeTap),
      // Minutele de voce s-au terminat și rămas-bunul s-a rostit (F-06):
      // butonul de microfon redevine inactiv, iar nota spune că se continuă
      // în scris - transcriptul e deja în fir, nimic nu se pierde.
      onQuotaEnded: () => {
        widget.setVoiceActive(false);
        widget.addNotice(copy.voiceMinutesOver);
      },
    };
    voiceSessionRef = voiceOptions;
    voice = new VoiceController(voiceOptions);

    // Aceeași instanță, doar nenulă pentru compilator: `voice` e vizibilă și în
    // afara blocului (partea de commerce o citește), deci acolo e nullabilă.
    const voiceController = voice;
    widget.onVoiceToggle(() => {
      markInteractionStarted();
      if (voiceController.active) {
        widget.setVoiceActive(false);
        void voiceController.stop();
        return;
      }
      widget.setVoiceActive(true);
      // Vocea se recitește la fiecare pornire, nu doar la boot: alegerea se
      // poate schimba în mijlocul tabului (comanda `/voice-2` scrisă în card,
      // vezi `orbchat/index.ts`), și scrie în ACELAȘI storage ca `?voice=`.
      voiceOptions.voiceName = voiceNameRequested(branding);
      voiceController.start().catch((err: Error) => {
        widget.setVoiceActive(false);
        widget.addNotice(voiceErrorMessage(err, copy));
      });
    });

    // O navigare care descarcă documentul omoară conexiunea la ElevenLabs în
    // mijlocul cuvântului. De-aia tura care navighează e MUTĂ (promptul o cere)
    // și de-aia lăsăm aici, în ultima clipă dinaintea saltului, semnul din care
    // pagina următoare reia ACEEAȘI sesiune. Se armează o singură dată, la init:
    // saltul poate veni de la orice acțiune, iar vocea poate porni oricând.
    // Nu se ciocnește cu `armPageChange` (continuarea chatului SCRIS): acela nu
    // se armează cât vocea e activă, iar ăsta nu scrie nimic dacă nu e.
    executor.onReloadNavigation(() => voiceController.markResumeAcrossReload());

    // Cealaltă jumătate: am ajuns pe pagina nouă exact dintr-o navigare vocală.
    // Fără await - `init` nu ține încărcarea paginii clientului în loc.
    void voiceController.resumeAfterReload().then((resumed) => {
      // Butonul de microfon trebuie să arate ce e adevărat: conversația chiar
      // merge mai departe, deși vizitatorul n-a apăsat nimic pe pagina asta.
      if (resumed) widget.setVoiceActive(true);
    });
  }

  // Excluderea pe URL trebuie să urmărească și navigarea FĂRĂ reload (Angular
  // cu pushState, hash-routing) - un singur load de pagină poate traversa
  // pagini permise și excluse. Patch-uim history DOAR când există listă de
  // excluderi: n-avem ce căuta în history API-ul sitului clientului degeaba.
  if (excludedUrls.length > 0) {
    applyHiddenState();
    window.addEventListener("hashchange", applyHiddenState);
    window.addEventListener("popstate", applyHiddenState);
    for (const method of ["pushState", "replaceState"] as const) {
      const original = window.history[method].bind(window.history);
      window.history[method] = (...args: Parameters<History["pushState"]>) => {
        original(...args);
        applyHiddenState();
      };
    }
  }

  // Spotlight-ul nu cunoaște widgetul (și nici n-ar avea cum: panoul e `fixed`
  // înăuntrul shadow root-ului, deci nu stă în cutia lui `.aig-root`) - le legăm
  // aici, unde le deținem pe amândouă.
  setChatObstacle({
    rect: () => widget.panelRect(),
    shift: (dx, dy) => widget.shiftPanel(dx, dy),
    // Orb-chat: bula vocală crește cu replica, deci ferirea decisă la aprindere
    // expiră - suprafața anunță, ghidarea re-decide. Panoul clasic n-are metoda.
    ...(widget.onPanelChange ? { onChange: (l: () => void) => widget.onPanelChange!(l) } : {}),
    // Telefon: panoul clasic se poate cobori până rămâne doar headerul.
    ...(widget.setDocked ? { setDocked: (d: boolean) => widget.setDocked!(d) } : {}),
    ...(widget.onDockRelease ? { onDockRelease: (l: () => void) => widget.onDockRelease!(l) } : {}),
  });

  // firul de discuție supraviețuiește navigării/refresh-ului (sessionStorage,
  // per tab) - la prima vizită din acest tab pornim cu salutul
  const greeting = branding.welcomeMessage || copy.greeting;
  const history = loadChatHistory(opts.appId);
  if (history.length > 0) {
    for (const msg of history) widget.addMessage(msg.role, msg.text);
  } else {
    widget.addMessage("agent", greeting);
    pendingGreeting = greeting;
    // Rar: montarea a restaurat deja panoul deschis (widget.ts:382), ÎNAINTE ca
    // salutul să fie cunoscut aici - interacțiunea a început deja, deci se
    // persistă acum, nu la următoarea deschidere/întrebare (care poate să nu
    // mai vină în acest tab).
    if (interactionStarted) persistPendingGreeting();
  }

  /**
   * „Șterge conversația și începe din nou" (butonul din suprafață, confirmat
   * inline acolo). Aici se face ce e ireversibil, în ordinea în care contează:
   *
   *  1. vocea, dacă vorbește - o conversație care continuă peste un fir șters ar
   *     răspunde din context pe care vizitatorul tocmai a cerut să-l uite;
   *  2. ghidarea de pe pagină - inelul rămas ar fi pasul unei conversații care
   *     nu mai există;
   *  3. istoricul local + intenția de continuare din `sessionStorage`;
   *  4. un id de sesiune NOU: conversația de pe server e legată de el, deci ăsta
   *     e singurul lucru care resetează și ce vede modelul.
   *
   * Ce NU se atinge, deliberat: contoarele motorului de nudge (G24). Resetul e
   * despre conversație, nu despre cât de des are voie asistentul să vorbească
   * neîntrebat - o resetare care ar reîncărca plafoanele ar transforma butonul
   * într-un mod de a cere nudge-uri la infinit.
   */
  widget.onReset?.(() => {
    if (voice?.active) {
      widget.setVoiceActive(false);
      void voice.stop();
    }
    disarmSpotlightActivation("abandoned_dismissed");
    dismissSpotlight();
    clearChatHistory(opts.appId);
    takePendingContinuation(opts.appId);
    const sessionId = resetChatSessionId(opts.appId);
    if (voiceSessionRef) voiceSessionRef.sessionId = sessionId;
    widget.resetThread?.(greeting);
    // Exact starea unui tab proaspăt: salutul e din nou „în așteptare", scris
    // abia la următoarea interacțiune reală. Vezi `pendingGreeting`.
    pendingGreeting = greeting;
    if (interactionStarted) persistPendingGreeting();
  });

  // Am ajuns aici pentru că vizitatorul a apăsat linkul evidențiat la turul
  // anterior și pagina s-a reîncărcat: răspundem la aceeași întrebare, acum că
  // pagina promisă e în față. Fără await - `init` nu trebuie să țină încărcarea
  // paginii clientului în loc. (TTL-ul din takePendingContinuation aruncă
  // intențiile prea vechi ca să mai fie sosirea unui click recent.)
  const pending = takePendingContinuation(opts.appId);
  if (pending) {
    // deschis: răspunsul vine nesolicitat, iar un panou închis l-ar ascunde
    // tocmai în momentul în care omul așteaptă continuarea
    widget.open();
    // "reached": linkul evidențiat la turul anterior chiar a dus aici - asta
    // e dovada, pe calea de navigare (MPA). Fără targetLabel: pasul de
    // dinaintea reload-ului nu mai există în acest document, iar eticheta lui
    // nu s-ar putea recupera fără riscul de a arăta text învechit. Exclusivă
    // cu "reached"-ul de mai sus (settled/in_page_reveal): un `pending` ajuns
    // aici a fost fie consumat deja acolo (nu mai există în storage), fie nu
    // a fost - niciodată ambele.
    void api
      .event({
        outcome: "reached",
        path: currentPagePath(),
        sessionId: ensureChatSessionId(opts.appId),
        t: Date.now(),
      })
      .catch(() => {});
    void runAsk(pending.question, { hops: pending.hops, reason: "navigate" });
  }
}
